import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import CiteSummary from "@/components/blog/CiteSummary";

const BLOG_POSTING_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": "What a CLIA Gap Audit Actually Finds",
  "url": "https://labcomplyx.com/blog/clia-gap-audit",
  "mainEntityOfPage": "https://labcomplyx.com/blog/clia-gap-audit",
  "datePublished": "2026-07-20",
  "dateModified": "2026-07-20",
  "author": { "@id": "https://labcomplyx.com/#founder" },
  "publisher": { "@id": "https://labcomplyx.com/#organization" },
};

export default function CLIAGapAudit() {
  return (
    <>
      <SEO
        title="What a CLIA Gap Audit Actually Finds | LabComplyx Blog"
        description="The most common documentation gaps CLIA inspectors identify — and how clinical labs resolve them before citations. A practical guide from LabComplyx."
        canonical="https://labcomplyx.com/blog/clia-gap-audit"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BLOG_POSTING_SCHEMA)}</script>
      </Helmet>
      <div className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/blog" className="text-xs text-muted-foreground hover:text-primary transition-colors">← Back to Blog</Link>
          </div>
          <span className="inline-block text-xs font-medium text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">CLIA · Clinical Labs</span>
          <h1 className="text-4xl font-bold text-foreground mb-5">What a CLIA Gap Audit Actually Finds</h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-10">
            Most CLIA citations are preventable. The documentation gaps that show up during inspections are usually not surprises — they are the same categories of issues that appear repeatedly across clinical labs. Here is what auditors look for first, and how labs typically resolve them.
          </p>

          <CiteSummary
            heading="TL;DR — CLIA Gap Audit Snapshot"
            items={[
              "The five most common CLIA documentation gaps are: outdated or missing SOPs, incomplete competency assessment records, QC log irregularities, CAPAs without documented closure, and missing critical value notification records.",
              "Outdated SOPs are the single most common CLIA citation category — inspectors verify currency, version control, and evidence of annual review.",
              "CLIA requires documented competency assessments for all testing personnel using six specific evaluation elements, at defined intervals.",
              "Critical value documentation must record who was notified, when, and what action was taken — gaps here are both a compliance and patient safety risk.",
            ]}
          />

          <div className="prose prose-sm max-w-none space-y-8 text-foreground">
            <section>
              <h2 className="text-xl font-bold mb-3">1. Outdated or missing SOPs</h2>
              <p className="text-muted-foreground leading-relaxed">The most common CLIA citation category. SOPs that haven't been reviewed in over 12 months, reference discontinued equipment, or lack required signatures trigger immediate deficiency reports. Inspectors verify currency, version control, and evidence of annual review.</p>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">2. Competency assessment documentation gaps</h2>
              <p className="text-muted-foreground leading-relaxed">CLIA requires documented competency assessments for all testing personnel, at defined intervals, using six specific evaluation elements. Missing records, incomplete evaluations, or assessments that don't match the lab's actual test menu are common findings.</p>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">3. QC documentation irregularities</h2>
              <p className="text-muted-foreground leading-relaxed">Quality control logs that are incomplete, don't reflect corrective actions taken, or show patterns of out-of-range values without documented follow-up are a significant inspection risk. Inspectors look for consistency and completeness across the review period.</p>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">4. CAPA records without closed corrective actions</h2>
              <p className="text-muted-foreground leading-relaxed">Open CAPAs with no documented closure, effectiveness checks that were never completed, or corrective actions that lack root-cause traceability are common findings — especially in labs that have experienced high staff turnover.</p>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">5. Test result reporting and critical value documentation</h2>
              <p className="text-muted-foreground leading-relaxed">Documentation of critical value notifications — including who was notified, when, and what action was taken — is a required CLIA element. Incomplete records in this area represent both a compliance risk and a patient safety concern.</p>
            </section>
          </div>

          <div className="mt-12 p-7 bg-muted/30 border border-border rounded-xl">
            <h3 className="font-semibold text-foreground mb-3">Is your lab ready for a CLIA inspection?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">LabComplyx offers a free CLIA Gap Audit to identify the top documentation gaps in your lab and outline a clear path to inspection readiness.</p>
            <Link to="/validation-intake"><Button className="gap-2">Book a Free CLIA Gap Audit <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>

          <div className="mt-8 pt-8 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-3">Related resources</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/blog/cap-inspection-readiness" className="text-xs font-medium text-primary hover:underline">CAP Inspection Readiness: A 90-Day Checklist →</Link>
              <Link to="/industries/clinical-diagnostic" className="text-xs font-medium text-primary hover:underline">Clinical & Diagnostic Labs →</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}